import React from 'react';
import { motion } from 'framer-motion';
import { SparkleIcon, ArrowRightIcon, UsersIcon, FileTextIcon, TargetIcon, AlertTriangleIcon, CheckIcon, ChevronRightIcon, ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { OPERATOR_AVATAR, solutionIntro, solutionInputs, atnaCard, solutionChannels, intelligenceSteps, solutionClosing, type Bi } from '../../data/communicationsSolution';
import { SolutionInputCard, SolutionChannelCard, LiveResultsPanel } from './solution/SolutionCards';
const EASE = [0.23, 1, 0.32, 1] as const;
const ROW_ICONS = {
  audience: UsersIcon,
  notice: FileTextIcon,
  channels: TargetIcon,
  check: AlertTriangleIcon
} as const;
const rows = (n: number) => Array.from({
  length: n
}, (_, i) => (i + 0.5) / n * 100);
const IN_Y = rows(4);
const CH_Y = rows(6);
const TONES = ['#8B7FF7', '#6D5DF6', '#7C70F2', '#9A8CF8', '#6D5DF6', '#8B7FF7'];
function curve(x1: number, y1: number, x2: number, y2: number) {
  const mx = (x1 + x2) / 2;
  return `M${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}
function Flow({
  side
}: {
  side: 'in' | 'out';
}) {
  const paths = side === 'in' ? IN_Y.map((y, i) => ({
    d: curve(0, y, 100, 44 + i * 2.5),
    c: TONES[i]
  })) : CH_Y.map((y, i) => ({
    d: curve(0, 40 + i * 2.5, 100, y),
    c: TONES[i]
  }));
  return <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-2513" data-mp="2jpto1ltlufwi">
      {paths.map((p, i) => <path key={i} d={p.d} fill="none" stroke={p.c} strokeWidth="1.4" vectorEffect="non-scaling-stroke" data-id="element-2514" data-mp="2aqop4ee4skjt" data-mpi={i} />)}
      {paths.map((p, i) => {
      const y = side === 'in' ? IN_Y[i] : 40 + i * 2.5;
      return <circle key={`d${i}`} cx={side === 'in' ? 1 : 0.5} cy={y} r="0.9" fill="#6D5DF6" data-id="element-2515" data-mp="210lygkuhfion" data-mpi={i} />;
    })}
    </svg>;
}
function AtnaEngine({
  isFR
}: {
  isFR: boolean;
}) {
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <div className="relative h-full rounded-3xl border border-[#E7E5F8] bg-white px-5 pt-10 pb-5 flex flex-col shadow-[0_30px_60px_-36px_rgba(79,70,229,0.45)]" data-id="element-2516" data-mp="1rz5mi8o0r8ib">
      <span className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border border-[#E7E5F8] shadow-[0_10px_24px_-12px_rgba(79,70,229,0.6)] flex items-center justify-center" aria-hidden="true" data-id="element-2517" data-mp="3oz66hagf9ol7">
        <SparkleIcon className="w-6 h-6 text-[#4F46E5] fill-[#4F46E5]" strokeWidth={1.5} data-id="element-2518" data-mp="3fz12zbecnacb" />
      </span>
      <p className="text-center font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-2519" data-mp="gi1myy0gz9e3">{pick(atnaCard.label)}</p>
      <p className="text-center font-sans text-[13px] text-[#6B7180] mt-1.5" data-id="element-2520" data-mp="bhx4j2g044l8">{pick(atnaCard.verbs)}</p>

      <div className="mt-5 rounded-xl border border-[#E6E7EE] bg-white px-3 py-3 flex items-center gap-3 shadow-[0_6px_16px_-14px_rgba(20,20,60,0.4)]" data-id="element-2521" data-mp="7kixnhfsm8we">
        <img src={OPERATOR_AVATAR} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-2522" data-mp="2s9ot09b76y8y" />
        <p className="flex-1 font-sans text-[13px] text-[#121212] leading-snug" data-id="element-2523" data-mp="2n8iyq71w32xb">{pick(atnaCard.command)}</p>
        <span className="w-8 h-8 rounded-full bg-[#4F46E5] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2524" data-mp="12st8uf2mmkhr">
          <ArrowRightIcon className="w-4 h-4 text-white" strokeWidth={2.2} data-id="element-2525" data-mp="17qur4xs8afs6" />
        </span>
      </div>

      <ul className="mt-3 flex-1 flex flex-col gap-2" data-id="element-2526" data-mp="1bq3kw2n9qdnq">
        {atnaCard.rows.map(r => {
        const Icon = ROW_ICONS[r.key];
        return <li key={r.key} className="flex-1 rounded-xl bg-[#FAFAFD] border border-[#F0F0F5] px-4 py-2.5 flex items-center gap-3.5" data-id="element-2527" data-mp="1listmprfpdfd">
              <Icon className={`w-5 h-5 shrink-0 ${r.warn ? 'text-[#E23E3E]' : 'text-[#4F46E5]'}`} strokeWidth={1.7} aria-hidden="true" data-id="element-2528" data-mp="1qf9ijm6gjvti" />
              <span className="flex-1 min-w-0" data-id="element-2529" data-mp="1zhirw8pzpj5b">
                <span className="block font-sans text-[14px] font-medium text-[#121212]" data-id="element-2530" data-mp="1zh8zkehh2zwg">{pick(r.title)}</span>
                <span className="block font-sans text-[12.5px] text-[#5B6478] mt-0.5" data-id="element-2531" data-mp="13z9owi9ot4i">{pick(r.sub)}</span>
              </span>
              {r.warn ? <ChevronRightIcon className="w-4 h-4 text-[#9AA0AE] shrink-0" aria-hidden="true" data-id="element-2532" data-mp="35yfrte0hq3xh" /> : <span className="w-5 h-5 rounded-full bg-[#22A04F] flex items-center justify-center shrink-0" aria-label={isFR ? 'Vérifié' : 'Verified'} data-id="element-2533" data-mp="31wc8623o3t14">
                  <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} aria-hidden="true" data-id="element-2534" data-mp="3ay26lrvyvs8e" />
                </span>}
            </li>;
      })}
      </ul>

      <button type="button" className="mt-3 w-full rounded-xl bg-[#4F46E5] py-3 font-sans text-[14px] font-medium text-white inline-flex items-center justify-center gap-2 transition-colors duration-150 hover:bg-[#4338CA]" data-id="element-2535" data-mp="2n6eq83wbzzkd">
        {pick(atnaCard.cta)}
        <ArrowRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-2536" data-mp="2s5zoec3og3w1" />
      </button>
    </div>;
}
export function CommunicationsSolution() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  const bodyLines = isFR ? solutionIntro.body.fr : solutionIntro.body.en;
  const steps = <ol className="rounded-2xl border border-[#EEEEF4] bg-white px-4 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-0 shadow-[0_12px_30px_-26px_rgba(40,36,120,0.45)]" data-id="element-2537" data-mp="jrb39l41lqa3">
      {intelligenceSteps.map((s, i) => <li key={s.n} className="flex items-center gap-3 lg:px-3" data-id="element-2538" data-mp="a2kqrfbdmjwu" data-mpi={i}>
          <span className="w-11 h-11 rounded-full bg-[#F1F0FD] flex items-center justify-center font-sans text-[14px] font-medium text-[#4F46E5] shrink-0" data-id="element-2539" data-mp="xrluip99l6vs">
            {s.n}
          </span>
          <span className="flex-1 min-w-0" data-id="element-2540" data-mp="oqxp2ixxseui">
            <span className="block font-sans text-[15px] font-medium text-[#121212]" data-id="element-2541" data-mp="opw1jezqhzvw">{pick(s.title)}</span>
            <span className="block font-sans text-[12.5px] text-[#5B6478] leading-snug mt-0.5" data-id="element-2542" data-mp="to7c5rxusef6">{pick(s.body)}</span>
          </span>
          {i < intelligenceSteps.length - 1 && <ArrowRightIcon className="hidden lg:block w-4 h-4 text-[#A3A8B6] shrink-0" aria-hidden="true" data-id="element-2543" data-mp="3eg9f6zuwr6td" />}
        </li>)}
    </ol>;
  return <div className="relative max-w-[1600px] mx-auto" data-id="element-2544" data-mp="1k1vo1krvqnwt">
      <div className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 w-[760px] h-[520px] rounded-full bg-[#EFEEFD] opacity-70 blur-[130px]" aria-hidden="true" data-id="element-2545" data-mp="1f1pq5mv0o56a" />

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} className="relative text-center max-w-6xl mx-auto" data-id="element-2546" data-mp="168w67c4mkhm3">
        <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-2547" data-mp="wcjcie92y3rc">{pick(solutionIntro.eyebrow)}</p>
        <h2 className="font-serif text-[2.2rem] sm:text-5xl xl:text-[3.5rem] text-[#121212] leading-[1.08] tracking-tight mt-5" data-id="element-2548" data-mp="naa5hyd0ssip">
          {pick(solutionIntro.title)} <span className="text-[#4F46E5]" data-id="element-2549" data-mp="2lmsbh2rzqq07">{pick(solutionIntro.accent)}</span>
        </h2>
        <p className="font-sans text-[16px] md:text-[17px] text-[#5B6478] leading-relaxed mt-5" data-id="element-2550" data-mp="2hlf3am1feaq4">
          {bodyLines.map(l => <span key={l} className="xl:block" data-id="element-2551" data-mp="8elhec9y5w3b">
              {l}{' '}
            </span>)}
        </p>
      </motion.div>

      {/* Desktop orchestration */}
      <motion.div initial={{
      opacity: 0,
      y: 14
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.3,
      delay: 0.06,
      ease: EASE
    }} className="relative hidden xl:block mt-16" data-id="element-2552" data-mp="d7oa6i6wh1ig">
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,0.26fr)_minmax(0,1.3fr)_minmax(0,0.26fr)_minmax(0,0.78fr)_minmax(0,0.9fr)] gap-y-0 items-stretch h-[500px]" data-id="element-2553" data-mp="3llwxbaqnl038">
          <div className="flex flex-col gap-3" data-id="element-2554" data-mp="3umbjsdfcsl8b">
            {solutionInputs.map(it => <div key={it.key} className="flex-1" data-id="element-2555" data-mp="14xj12jaeoz98">
                <SolutionInputCard isFR={isFR} item={it} data-id="element-2556" data-mp="14yu3ofs480yr" />
              </div>)}
          </div>
          <Flow side="in" data-id="element-2557" data-mp="19xnjieuvk423" />
          <AtnaEngine isFR={isFR} data-id="element-2558" data-mp="liqzlboehgkr" />
          <Flow side="out" data-id="element-2559" data-mp="lk2114udic8p" />
          <div className="flex flex-col gap-2.5" data-id="element-2560" data-mp="lh61xd6shhio">
            {solutionChannels.map(c => <div key={c.key} className="flex-1" data-id="element-2561" data-mp="gi3164xudxgi">
                <SolutionChannelCard isFR={isFR} item={c} data-id="element-2562" data-mp="ubudfl2yjvfx" />
              </div>)}
          </div>
          <div className="pl-5" data-id="element-2563" data-mp="pjsytsibhsmq">
            <LiveResultsPanel isFR={isFR} data-id="element-2564" data-mp="pird8u3odnqr" />
          </div>
        </div>
        <div className="mt-4 mx-auto w-[72%]" data-id="element-2565" data-mp="148gr9pwv9m8g">{steps}</div>
      </motion.div>

      {/* Mobile / tablet stack */}
      <div className="relative xl:hidden mt-14 space-y-5" data-id="element-2566" data-mp="zafaxcqts10o">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2567" data-mp="1daq2khzyljmw">
          {solutionInputs.map(it => <SolutionInputCard key={it.key} isFR={isFR} item={it} data-id="element-2568" data-mp="199wffqucc8v2" />)}
        </div>
        <ArrowDownIcon className="w-5 h-5 text-[#4F46E5] mx-auto" strokeWidth={1.5} aria-hidden="true" data-id="element-2569" data-mp="3f11jy33q5ckc" />
        <div className="max-w-[560px] mx-auto pt-4" data-id="element-2570" data-mp="3b19isbgnu8p0">
          <AtnaEngine isFR={isFR} data-id="element-2571" data-mp="3b2kncdhxjkh6" />
        </div>
        <ArrowDownIcon className="w-5 h-5 text-[#4F46E5] mx-auto" strokeWidth={1.5} aria-hidden="true" data-id="element-2572" data-mp="30xccobiog9o5" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-id="element-2573" data-mp="34wl439q8vvgj">
          {solutionChannels.map(c => <SolutionChannelCard key={c.key} isFR={isFR} item={c} data-id="element-2574" data-mp="2r67y3cwwyqtu" />)}
        </div>
        <div className="max-w-[560px] mx-auto" data-id="element-2575" data-mp="2w3q876mo3c3f">
          <LiveResultsPanel isFR={isFR} data-id="element-2576" data-mp="2h7sgq1nouvxj" />
        </div>
        {steps}
      </div>

      {/* Closing */}
      <div className="relative mt-16 lg:mt-20 flex items-center gap-6" data-id="element-2577" data-mp="2m7wo926mcgbm">
        <span className="hidden md:block flex-1 h-px bg-[#E6E7EE]" aria-hidden="true" data-id="element-2578" data-mp="287lunrdxcnmr" />
        <div className="text-center max-w-3xl mx-auto" data-id="element-2579" data-mp="289ylyv5koach">
          <p className="font-serif text-[1.7rem] md:text-[2.2rem] text-[#121212] leading-snug" data-id="element-2580" data-mp="2d7qftups26jb">{pick(solutionClosing.title)}</p>
          <p className="font-sans text-[15px] text-[#5B6478] mt-2" data-id="element-2581" data-mp="6h0kyc1n6bgg">{pick(solutionClosing.body)}</p>
        </div>
        <span className="hidden md:block flex-1 h-px bg-[#E6E7EE]" aria-hidden="true" data-id="element-2582" data-mp="bcziap4y73t0" />
      </div>
    </div>;
}